import { z } from "zod/v4";

import { BaseRequestArgs } from "../base-reader";

export const KnownCampaignTypeNameSchema = z.enum([
  "fee_rebate",
  "maker_incentive",
  "liquidation_rebate",
  "volume_milestone",
  "first_funded_trial",
  "affiliate",
  "discretionary",
]);

export type KnownCampaignTypeName = z.infer<typeof KnownCampaignTypeNameSchema>;

export const CampaignTypeNameSchema = z.union([KnownCampaignTypeNameSchema, z.string()]);

export type CampaignTypeName = z.infer<typeof CampaignTypeNameSchema>;

export const KnownCampaignStatusNameSchema = z.enum([
  "draft",
  "funded",
  "active",
  "expired",
  "reclaimed",
  "cancelled",
]);

export type KnownCampaignStatusName = z.infer<typeof KnownCampaignStatusNameSchema>;

export const CampaignStatusNameSchema = z.union([KnownCampaignStatusNameSchema, z.string()]);

export type CampaignStatusName = z.infer<typeof CampaignStatusNameSchema>;

const KNOWN_CAMPAIGN_TYPES: ReadonlySet<string> = new Set(KnownCampaignTypeNameSchema.options);

export function isKnownCampaignType(type: CampaignTypeName): type is KnownCampaignTypeName {
  return KNOWN_CAMPAIGN_TYPES.has(type);
}

const KNOWN_CAMPAIGN_STATUSES: ReadonlySet<string> = new Set(KnownCampaignStatusNameSchema.options);

export function isKnownCampaignStatus(
  status: CampaignStatusName,
): status is KnownCampaignStatusName {
  return KNOWN_CAMPAIGN_STATUSES.has(status);
}

export const CampaignMetadataHttpSchema = z
  .object({
    campaign_id: z.coerce.number(),
    campaign_type: CampaignTypeNameSchema,
    status: CampaignStatusNameSchema,
    title: z.string(),
    reward_asset: z.string(),
    start_ts_sec: z.coerce.number(),
    end_ts_sec: z.coerce.number(),
    claim_start_ts_sec: z.coerce.number(),
    claim_end_ts_sec: z.coerce.number(),
    total_funded: z.coerce.number(),
    description: z.string().nullish(),
  })
  .transform((v) => ({
    campaignId: v.campaign_id,
    campaignType: v.campaign_type,
    status: v.status,
    title: v.title,
    rewardAsset: v.reward_asset,
    startTsSec: v.start_ts_sec,
    endTsSec: v.end_ts_sec,
    claimStartTsSec: v.claim_start_ts_sec,
    claimEndTsSec: v.claim_end_ts_sec,
    totalFunded: v.total_funded,
    description: v.description ?? undefined,
  }));
export type CampaignMetadataHttp = z.infer<typeof CampaignMetadataHttpSchema>;

// u64 raw chain units; FE divides by 10^6 for USDC.
// readyToClaim = claimableAmount - claimedAmount - in-flight; use it for "Claim $X" CTAs.
export const CampaignClaimSchema = z
  .object({
    campaign_id: z.coerce.number(),
    campaign_type: CampaignTypeNameSchema,
    status: CampaignStatusNameSchema,
    title: z.string(),
    reward_asset: z.string(),
    start_ts_sec: z.coerce.number(),
    end_ts_sec: z.coerce.number(),
    claim_start_ts_sec: z.coerce.number(),
    claim_end_ts_sec: z.coerce.number(),
    total_funded: z.coerce.number(),
    description: z.string().nullish(),
    has_allocation: z.boolean(),
    claimable_amount: z.coerce.number(),
    claimed_amount: z.coerce.number(),
    ready_to_claim: z.coerce.number(),
    claimed_at_ts_sec: z.coerce.number().nullish(),
    claim_tx_hash: z.string().nullish(),
  })
  .transform((v) => ({
    campaignId: v.campaign_id,
    campaignType: v.campaign_type,
    status: v.status,
    title: v.title,
    rewardAsset: v.reward_asset,
    startTsSec: v.start_ts_sec,
    endTsSec: v.end_ts_sec,
    claimStartTsSec: v.claim_start_ts_sec,
    claimEndTsSec: v.claim_end_ts_sec,
    totalFunded: v.total_funded,
    description: v.description ?? undefined,
    hasAllocation: v.has_allocation,
    claimableAmount: v.claimable_amount,
    claimedAmount: v.claimed_amount,
    readyToClaim: v.ready_to_claim,
    claimedAtTsSec: v.claimed_at_ts_sec,
    claimTxHash: v.claim_tx_hash,
  }));
export type CampaignClaim = z.infer<typeof CampaignClaimSchema>;

export const WeeklyEarningSchema = z
  .object({
    week_start_ts_sec: z.coerce.number(),
    reward_amount: z.coerce.number(),
  })
  .transform((v) => ({
    weekStartTsSec: v.week_start_ts_sec,
    rewardAmount: v.reward_amount,
  }));
export type WeeklyEarning = z.infer<typeof WeeklyEarningSchema>;

export const TypeBreakdownSchema = z
  .object({
    campaign_type: CampaignTypeNameSchema,
    lifetime_earned: z.coerce.number(),
    ready_to_claim: z.coerce.number(),
    total_claimed: z.coerce.number(),
  })
  .transform((v) => ({
    campaignType: v.campaign_type,
    lifetimeEarned: v.lifetime_earned,
    readyToClaim: v.ready_to_claim,
    totalClaimed: v.total_claimed,
  }));
export type TypeBreakdown = z.infer<typeof TypeBreakdownSchema>;

// lifetimeEarned = readyToClaim + totalClaimed.
// weeklyWowBps: cumulative WoW basis points; see handle_campaigns.rs:494-497. 0 when prior cumulative is 0 or growth is non-positive.
export const CampaignSummarySchema = z
  .object({
    lifetime_earned: z.coerce.number(),
    ready_to_claim: z.coerce.number(),
    total_claimed: z.coerce.number(),
    breakdown_by_type: z.array(TypeBreakdownSchema),
    claims: z.array(CampaignClaimSchema),
    year_to_date: z.coerce.number(),
    weekly_wow_bps: z.number(),
    weekly_breakdown: z.array(WeeklyEarningSchema),
    total_claims: z.coerce.number(),
  })
  .transform((v) => ({
    lifetimeEarned: v.lifetime_earned,
    readyToClaim: v.ready_to_claim,
    totalClaimed: v.total_claimed,
    breakdownByType: v.breakdown_by_type,
    claims: v.claims,
    yearToDate: v.year_to_date,
    weeklyWowBps: v.weekly_wow_bps,
    weeklyBreakdown: v.weekly_breakdown,
    totalClaims: v.total_claims,
  }));
export type CampaignSummary = z.infer<typeof CampaignSummarySchema>;

export const ActiveCampaignsSchema = z.array(CampaignMetadataHttpSchema);

export type GetActiveCampaignsArgs = BaseRequestArgs;

export interface GetCampaignSummaryArgs extends BaseRequestArgs {
  accountAddress: string;
  limit?: number;
  offset?: number;
}
